import { env } from '@/config/environment';
import api from './api';

export interface MonitoringRegionOption {
  label: string;
  value: string;
}

export interface MonitoringRegionsResponse {
  monitoring_regions: MonitoringRegionOption[];
}

export interface TimezoneOption {
  label: string;
  value: string;
  abbr: string;
  suggested_region?: string;
}

export interface TimezonesResponse {
  timezones: TimezoneOption[];
}

export type MonitorStatus = 'UP' | 'DOWN' | 'PAUSED' | 'EXPIRING-DOMAIN' | 'EXPIRING-SSL' ;
export interface Site {
  id: number;
  url: string;
  name: string;
  enabled: boolean;
  deleted: boolean;
  monitor_frequency: number;
  timeout: number;
  created_at: string;
  created_by: string | null;
  response_time_threshold: number;
  updated_at: string;
  hourly_data: number[] | null;
  uptime_24h: number | null;
  current_status: MonitorStatus;
  notification_email?: string | null;
  timezone: string;
  is_local_timezone?: boolean; // false = use site timezone, true = use local timezone
  monitoring_region?: string;
  email_actions?: {
    http_alerts: boolean;
    incident_alerts: boolean;
    threshold_alerts: boolean;
    ssl_alerts: boolean;
  };
  uptime_duration?: number;
  uptime_duration_formatted?: string;
  next_maintenance_window?: MaintenanceWindow | null;
  /**
   * Backend v2+ structure: notification-related settings are nested under `email_settings`.
   * Backward compatibility is handled by normalizing older responses into this shape.
   */
  email_settings?: {
    http_status_codes: number[];
    alert_notification_email: string | null;
    threshold_breaches_required: number;
    http_breaches_required?: number | null;
    threshold_notification_email: string | null;
    check_ssl_errors: boolean;
    ssl_expiry_reminders: boolean;
    domain_expiry_reminders: boolean;
    expiry_notification_days: number;
    ssl_notification_email: string | null;
  };
}

export interface SitesResponse {
  total: number;
  sites: Site[];
}

export interface ActivityLog {
  id: number;
  site_id: number;
  site_url: string;
  region: string;
  check_time: string;
  status_code: number;
  response_time_ms: number;
  success: boolean;
  error_message: string | null;
  alert_id: number | null;
  incident_id: number | null;
  created_at: string;
}

export interface ActivityLogsFiltersApplied {
  site_id: number;
  status: string | null;
  origin: string | null;
  response_time_range: string | null;
  status_code: number | null;
  date_range: string | null;
}

export interface ActivityLogsResponse {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  next_page: number | null;
  previous_page: number | null;
  items_on_page: number;
  filters_applied: ActivityLogsFiltersApplied;
  data: ActivityLog[];
}

export interface UptimeStats {
  site_id: number;
  site_url?: string;
  site_name?: string;
  period_type: '24h' | '7d' | '30d' | '365d';
  period_start: string;
  period_end: string;
  uptime_percentage: number;
  total_incidents: number;
  total_downtime_seconds: number;
  total_downtime_formatted?: string;
  hourly_data: number[] | null;
  current_status: 'up' | 'down' | 'paused';
  current_status_duration_seconds: number;
  current_status_duration_formatted?: string;
  current_status_since?: string;
  calculated_at: string;
  last_incident_id?: string | null;
}

export interface BulkActionResult {
  site_id: number;
  success: boolean;
  message: string;
}

export interface BulkActionResponse {
  total_sites: number;
  successful: number;
  failed: number;
  results: BulkActionResult[];
}

export interface CurrentStatusResponse {
  site_id: number;
  site_url: string;
  site_name: string;
  status: string;
  status_emoji: string;
  duration_seconds: number;
  duration_formatted: string;
  since: string;
  message: string;
  domain_valid: boolean;
  ssl_cert_valid: boolean;
  ssl_cert_expiry: string;
  domain_expiry: string;
  next_maintenance_window?: MaintenanceWindow | null;
}

export interface ResponseTimeDataPoint {
  time: string;
  response_time: number;
}

export interface ResponseTimeGraphResponse {
  site_id: number;
  site_url: string;
  period: '1h' | '6h' | '24h' | '7d' | '30d';
  period_start: string;
  period_end: string;
  data_points: ResponseTimeDataPoint[];
  summary: {
    avg_response_time: number;
    min_response_time: number;
    max_response_time: number;
    total_checks: number;
  };
}

export interface PerformanceAnalysisQuarter {
  id: number;
  range: string;
  range_start: string;
  range_end: string;
  total_checks: number;
  thresholds_crossed: number;
  http_errors: number;
  threshold_percentage: number;
  error_percentage: number;
  correlation: 'LOW' | 'MEDIUM' | 'HIGH';
  correlation_percentage: number;
  health_status: 'HEALTHY' | 'WARNING' | 'DEGRADED' | 'CRITICAL';
  avg_response_time: number;
  max_response_time: number;
  min_response_time: number;
  common_error_codes: Array<{
    code: number;
    count: number;
  }>;
}

export interface PerformanceAnalysisResponse {
  site_id: number;
  site_name: string;
  site_url: string;
  period: '1d' | '7d' | '15d' | '30d';
  threshold_configured: number;
  analysis_start: string;
  analysis_end: string;
  quarters: PerformanceAnalysisQuarter[];
  summary: {
    total_checks: number;
    total_thresholds_crossed: number;
    total_http_errors: number;
    overall_threshold_rate: number;
    overall_error_rate: number;
    overall_correlation: 'LOW' | 'MEDIUM' | 'HIGH';
    overall_correlation_percentage: number;
    worst_quarter: {
      id: number;
      range: string;
      reason: string;
      severity: string;
    };
    insights: string[];
    recommendations: string[];
  };
}


export const getMonitoringRegions = async (): Promise<MonitoringRegionOption[]> => {
  const response = await api.get<MonitoringRegionsResponse>('/sites/options/monitoring-regions');
  const regions = response.monitoring_regions || [];
  
  return regions.map(region => ({
    label: region.label,
    value: region.value,
  }));
};

export const getTimezones = async (): Promise<TimezoneOption[]> => {
  const response = await api.get<TimezonesResponse>('/sites/options/timezones');
  return response.timezones || [];
};

export const getSites = async (): Promise<SitesResponse> => {
  return api.get<SitesResponse>('/sites', { include_24h_stats: 'true' });
};

export const getSiteById = async (id: number): Promise<Site> => {
  const response = await api.get<{ success: boolean; site: Site }>(`/sites/${id}`);
  return response.site;
};

export const startSite = async (id: number): Promise<void> => {
  await api.post(`/sites/${id}/start`);
};

export const stopSite = async (id: number): Promise<void> => {
  await api.post(`/sites/${id}/pause`);
};

export const deleteSite = async (id: number): Promise<void> => {
  await api.delete(`/sites/${id}`);
};

export interface SiteEmailActions {
  http_alerts: boolean;
  incident_alerts: boolean;
  threshold_alerts: boolean;
  ssl_alerts: boolean;
}

export interface CreateSiteRequest {
  url: string;
  name: string;
  monitor_frequency: number;
  timeout: number;
  enabled: boolean;
  response_time_threshold: number;
  ssl_expiry_reminders: boolean;
  domain_expiry_reminders: boolean;
  check_ssl_errors: boolean;
  created_by: string;
  timezone: string;
  email_actions: SiteEmailActions;
  notification_email?: string;
  alert_notification_email?: string;
  threshold_notification_email?: string;
  ssl_notification_email?: string;
  http_status_codes?: number[];
  threshold_breaches_required?: number;
  http_breaches_required?: number;
  expiry_notification_days?: number;
}

export interface EmailSettingsPayload {
  http_status_codes: number[];
  alert_notification_email: string | null;
  threshold_breaches_required: number;
  http_breaches_required?: number | null;
  threshold_notification_email: string | null;
  check_ssl_errors: boolean;
  ssl_expiry_reminders: boolean;
  domain_expiry_reminders: boolean;
  expiry_notification_days: number;
  ssl_notification_email: string | null;
}

export type EditSiteRequest = Omit<
  CreateSiteRequest,
  | 'http_status_codes'
  | 'alert_notification_email'
  | 'threshold_breaches_required'
  | 'threshold_notification_email'
  | 'check_ssl_errors'
  | 'ssl_expiry_reminders'
  | 'domain_expiry_reminders'
  | 'expiry_notification_days'
  | 'ssl_notification_email'
> & {
  email_settings: EmailSettingsPayload;
  is_local_timezone?: boolean; // false = use site timezone, true = use local timezone
};

export interface UpsertSiteResponse {
  success: boolean;
  site: Site;
}


export interface ActivityLogsFilters {
  status?: 'success' | 'failed' | 'all';
  status_code?: number | 'all';
  origin?: string | 'all';
  response_time_min?: number;
  response_time_max?: number;
  start_date?: string;
  end_date?: string;
  limit?: number;
  search?: string;
}

export const getActivityLogs = async (
  siteId: number,
  page: number = 1,
  pageSize: number = 10,
  filters?: ActivityLogsFilters
): Promise<ActivityLogsResponse> => {
  const params = new URLSearchParams({
    site_id: siteId.toString(),
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (filters) {
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters.status_code && filters.status_code !== 'all') {
      params.append('status_code', filters.status_code.toString());
    }
    if (filters.origin && filters.origin !== 'all') {
      params.append('origin', filters.origin);
    }
    if (filters.response_time_min !== undefined) {
      params.append('response_time_min', filters.response_time_min.toString());
    }
    if (filters.response_time_max !== undefined) {
      params.append('response_time_max', filters.response_time_max.toString());
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.limit !== undefined) {
      params.append('limit', filters.limit.toString());
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
  }

  return api.get<ActivityLogsResponse>(`/activity?${params.toString()}`);
};

export const exportActivityLogsCSV = async (
  siteId: number,
  filters?: ActivityLogsFilters
): Promise<Blob> => {
  // Build request body with filters
  const requestBody: Record<string, unknown> = {};
  
  if (filters) {
    if (filters.status && filters.status !== 'all') {
      requestBody.status = filters.status;
    }
    if (filters.status_code && filters.status_code !== 'all') {
      requestBody.status_code = filters.status_code;
    }
    if (filters.origin && filters.origin !== 'all') {
      requestBody.origin = filters.origin;
    }
    if (filters.response_time_min !== undefined) {
      requestBody.response_time_min = filters.response_time_min;
    }
    if (filters.response_time_max !== undefined) {
      requestBody.response_time_max = filters.response_time_max;
    }
    if (filters.start_date) {
      requestBody.start_date = filters.start_date;
    }
    if (filters.end_date) {
      requestBody.end_date = filters.end_date;
    }
    if (filters.limit !== undefined) {
      requestBody.limit = filters.limit;
    }
  }

  // Use direct fetch for blob response since api service defaults to JSON
  const CORE_API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL!;
  const url = `${CORE_API_BASE_URL}/sites/${siteId}/activity-logs/export/csv`;
  
  const token = typeof window !== 'undefined' ? localStorage.getItem(env.JWT_STORAGE_KEY) : null;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fetchResponse = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!fetchResponse.ok) {
    let errorMessage = `HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`;
    try {
      const errorData = await fetchResponse.json();
      if (errorData && typeof errorData === 'object') {
        const errorObj = errorData as Record<string, unknown>;
        if ('message' in errorObj && typeof errorObj.message === 'string') {
          errorMessage = errorObj.message;
        } else if ('detail' in errorObj && typeof errorObj.detail === 'string') {
          errorMessage = errorObj.detail;
        }
      }
    } catch {
      // If JSON parsing fails, use default error message
    }
    throw new Error(errorMessage);
  }

  return await fetchResponse.blob();
};

export const createSite = async (payload: CreateSiteRequest): Promise<Site> => {
  const response = await api.post<UpsertSiteResponse>('/sites', payload);
  return response.site;
};

export const editSite = async (id: number, payload: EditSiteRequest): Promise<Site> => {
  const response = await api.put<UpsertSiteResponse>(`/sites/${id}`, payload);
  return response.site;
};

export const getUptimeStats = async (
  siteId: number,
  period: '24h' | '7d' | '30d' | '365d'
): Promise<UptimeStats> => {
  return api.get<UptimeStats>(`/sites/${siteId}/uptime-stats?period=${period}`);
};

export const bulkStartSites = async (siteIds: number[]): Promise<BulkActionResponse> => {
  return api.post<BulkActionResponse>('/sites/bulk/start', { site_ids: siteIds });
};

export const bulkPauseSites = async (siteIds: number[]): Promise<BulkActionResponse> => {
  return api.post<BulkActionResponse>('/sites/bulk/pause', { site_ids: siteIds });
};

export const bulkDeleteSites = async (siteIds: number[]): Promise<BulkActionResponse> => {
  return api.post<BulkActionResponse>('/sites/bulk/delete', { site_ids: siteIds });
};

export const getCurrentStatus = async (siteId: number): Promise<CurrentStatusResponse> => {
  return api.get<CurrentStatusResponse>(`/sites/${siteId}/current-status`);
};

export const getResponseTimeGraph = async (
  siteId: number,
  period: '1h' | '6h' | '24h' | '7d' | '30d' = '24h'
): Promise<ResponseTimeGraphResponse> => {
  return api.get<ResponseTimeGraphResponse>(`/sites/${siteId}/response-time-graph?period=${period}`);
};

export const getPerformanceAnalysis = async (
  siteId: number,
  period: '1d' | '7d' | '15d' | '30d' = '1d'
): Promise<PerformanceAnalysisResponse> => {
  return api.get<PerformanceAnalysisResponse>(`/sites/${siteId}/performance-analysis?period=${period}`);
};

// Threshold Violations Drill-Down API Types
export interface ThresholdViolation {
  id: number;
  timestamp: string;
  response_time_ms: number;
  exceeded_by_ms: number;
  exceeded_by_percentage: number;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  status_code: number;
  success: boolean;
  error_message: string | null;
  alert_id: number | null;
  incident_id: number | null;
  region: string;
}

export interface ThresholdViolationsPagination {
  current_page: number;
  page_size: number;
  total_violations: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface ThresholdViolationsFilters {
  sort_by: string;
  sort_order: 'asc' | 'desc';
  min_exceeded_by: number | null;
  severity: string | null;
}

export interface ThresholdViolationsStatistics {
  avg_response_time: number;
  max_response_time: number;
  min_response_time: number;
  avg_exceeded_by_percentage: number;
  violations_with_failures: number;
  violations_without_failures: number;
}

export interface ThresholdViolationsQuarter {
  id: number;
  range: string;
  range_start: string;
  range_end: string;
  total_checks: number;
  thresholds_crossed: number;
  threshold_percentage: number;
  health_status: 'HEALTHY' | 'WARNING' | 'DEGRADED' | 'CRITICAL';
}

export interface ThresholdViolationsResponse {
  site_id: number;
  site_name: string;
  site_url: string;
  threshold_configured: number;
  period: '1d' | '7d' | '15d' | '30d';
  quarter: ThresholdViolationsQuarter;
  violations: ThresholdViolation[];
  pagination: ThresholdViolationsPagination;
  filters_applied: ThresholdViolationsFilters;
  statistics: ThresholdViolationsStatistics;
}

export const getThresholdViolations = async (
  siteId: number,
  quarterId: number,
  period: '1d' | '7d' | '15d' | '30d' = '1d',
  page: number = 1,
  pageSize: number = 50,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  minExceededBy?: number,
  severity?: string
): Promise<ThresholdViolationsResponse> => {
  const params = new URLSearchParams({
    period,
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  
  if (sortBy) params.append('sort_by', sortBy);
  if (sortOrder) params.append('sort_order', sortOrder);
  if (minExceededBy !== undefined) params.append('min_exceeded_by', minExceededBy.toString());
  if (severity) params.append('severity', severity);
  
  return api.get<ThresholdViolationsResponse>(
    `/sites/${siteId}/performance-analysis/quarters/${quarterId}/threshold-violations?${params.toString()}`
  );
};

export interface MaintenanceWindow {
  id: number;
  site_id: number;
  start_time: string; // ISO 8601 format: "2026-01-02T04:58:00+00:00"
  end_time: string; // ISO 8601 format: "2026-01-02T04:59:00+00:00"
  comments: string | null;
  reason: string | null;
  created_by: string | null;
  status: 'scheduled' | 'upcoming' | 'active' | 'completed' | 'cancelled';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility (derived from start_time/end_time)
  start_date?: string;
  start_time_legacy?: string;
  end_date?: string;
  end_time_legacy?: string;
  email_recipients?: string[];
}

export interface MaintenanceWindowResponse {
  maintenance_window: MaintenanceWindow | null;
}

export interface MaintenanceWindowHistoryResponse {
  success: boolean;
  site_id: number;
  site_name: string;
  site_url: string;
  email?: string | null; // Email at site_url level
  maintenance_windows: MaintenanceWindow[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

export interface CreateMaintenanceWindowRequest {
  start_time: string; // ISO 8601 format: "2025-12-25T02:00:00Z"
  end_time: string; // ISO 8601 format: "2025-12-25T06:00:00Z"
  comments?: string;
  reason?: string;
  created_by?: string;
}

export const createMaintenanceWindow = async (
  siteId: number,
  data: CreateMaintenanceWindowRequest
): Promise<MaintenanceWindowResponse> => {
  return api.post<MaintenanceWindowResponse>(`/maintenance_window/${siteId}`, data);
};

export const updateMaintenanceWindow = async (
  maintenanceWindowId: number,
  data: Partial<CreateMaintenanceWindowRequest>
): Promise<MaintenanceWindowResponse> => {
  return api.patch<MaintenanceWindowResponse>(`/maintenance_window/${maintenanceWindowId}`, data);
};

export const deleteMaintenanceWindow = async (
  maintenanceWindowId: number
): Promise<void> => {
  return api.delete(`/maintenance_window/${maintenanceWindowId}`);
};

export const getMaintenanceWindowHistory = async (
  siteId: number,
  page: number = 1,
  perPage: number = 10
): Promise<MaintenanceWindowHistoryResponse> => {
  return api.get<MaintenanceWindowHistoryResponse>(`/maintenance_window/${siteId}`, {
    page,
    per_page: perPage,
  });
};

// HTTP Errors Types and API
export interface HttpError {
  status_code: number;
  error_message: string;
  count: number;
  percentage: number;
  last_occurrence: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface HttpErrorsResponse {
  site_id: number;
  site_name: string;
  period: string;
  analysis_start: string;
  analysis_end: string;
  http_errors: HttpError[];
  pagination: {
    current_page: number;
    total_errors: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  filters_applied?: Record<string, unknown>;
  statistics: {
    total_checks: number;
    total_errors: number;
    error_rate: number;
    total_unique_errors: number;
    most_common_error: {
      status_code: number;
      count: number;
    };
    critical_errors_count: number;
    high_errors_count: number;
    error_diversity: string;
  };
}

export interface HttpErrorsParams {
  site_id: number;
  period?: '1d' | '7d' | '15d' | '30d';
  page?: number;
  page_size?: number;
  search?: string;
  start_date?: string;
  end_date?: string;
}

export const getHttpErrors = async (
  siteId: number,
  params?: Omit<HttpErrorsParams, 'site_id'>
): Promise<HttpErrorsResponse> => {
  const queryParams: Record<string, string> = {
    site_id: siteId.toString(),
  };

  if (params?.period) {
    queryParams.period = params.period;
  }
  if (params?.page) {
    queryParams.page = params.page.toString();
  }
  if (params?.page_size) {
    queryParams.page_size = params.page_size.toString();
  }
  if (params?.search) {
    queryParams.search = params.search;
  }
  if (params?.start_date) {
    queryParams.start_date = params.start_date;
  }
  if (params?.end_date) {
    queryParams.end_date = params.end_date;
  }

  return api.get<HttpErrorsResponse>(`/sites/${siteId}/http-errors`, queryParams);
};

// Threshold Violations Drill-Down Types and API
export interface ThresholdViolation {
  id: number;
  timestamp: string;
  response_time_ms: number;
  exceeded_by_ms: number;
  exceeded_by_percentage: number;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  status_code: number;
  success: boolean;
  error_message: string | null;
  alert_id: number | null;
  incident_id: number | null;
  region: string;
}

export interface ThresholdViolationsDrillDownResponse {
  site_id: number;
  site_name: string;
  site_url: string;
  threshold_configured: number;
  period: string;
  quarter: {
    id: number;
    range: string;
    range_start: string;
    range_end: string;
    total_checks: number;
    thresholds_crossed: number;
    threshold_percentage: number;
    health_status: string;
  };
  violations: ThresholdViolation[];
  pagination: {
    current_page: number;
    page_size: number;
    total_violations: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  filters_applied?: Record<string, unknown>;
  statistics: {
    avg_response_time: number;
    max_response_time: number;
    min_response_time: number;
    avg_exceeded_by_percentage: number;
    violations_with_failures: number;
    violations_without_failures: number;
  };
}

export interface ThresholdViolationsDrillDownParams {
  site_id: number;
  quarter_id: number;
  period: '1d' | '7d' | '15d' | '30d';
  page?: number;
  page_size?: number;
}

export const getThresholdViolationsDrillDown = async (
  siteId: number,
  quarterId: number,
  period: '1d' | '7d' | '15d' | '30d',
  page: number = 1,
  pageSize: number = 50,
  minExceededBy?: number,
  severity?: string
): Promise<ThresholdViolationsDrillDownResponse> => {
  const queryParams: Record<string, string> = {
    period,
    page: page.toString(),
    page_size: pageSize.toString(),
    limit: pageSize.toString(),
  };

  if (minExceededBy !== undefined) {
    queryParams.min_exceeded_by = minExceededBy.toString();
  }

  if (severity) {
    queryParams.severity = severity;
  }

  return api.get<ThresholdViolationsDrillDownResponse>(
    `/sites/${siteId}/performance-analysis/quarters/${quarterId}/threshold-violations`,
    queryParams
  );
};

// HTTP Errors Drill-Down Types and API
export interface HttpErrorOccurrence {
  timestamp: string;
  response_time_ms: number;
  error_message: string;
  region: string;
}

export interface HttpErrorDrillDown {
  status_code: number;
  error_message: string;
  count: number;
  percentage: number;
  last_occurrence: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
  occurrences: HttpErrorOccurrence[];
}

export interface HttpErrorsDrillDownResponse {
  site_id: number;
  site_name: string;
  site_url: string;
  threshold_configured: number;
  mode: 'historical' | 'quarter';
  analysis_start: string;
  analysis_end: string;
  period?: string;
  quarter?: {
    id: number;
    range: string;
    range_start: string;
    range_end: string;
    total_http_errors: number;
    error_percentage: number;
    health_status: string;
  };
  http_errors: HttpErrorDrillDown[];
  pagination: {
    current_page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    total_errors: number;
  };
  filters_applied?: {
    mode?: string;
    sort_by?: string;
    sort_order?: string;
    status_code?: string | null;
    severity?: string | null;
    min_percentage?: number | null;
    max_percentage?: number | null;
    min_count?: number | null;
    max_count?: number | null;
    quarter_id?: number;
    period?: string;
  };
  statistics: {
    total_checks: number;
    total_unique_errors: number;
    most_common_error: {
      status_code: number;
      count: number;
    };
    critical_errors_count: number;
    high_errors_count: number;
    error_diversity: string;
  };
}

export interface HttpErrorsDrillDownParams {
  site_id: number;
  quarter_id?: number;
  period?: '1d' | '7d' | '15d' | '30d';
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
  sort_by?: 'count' | 'percentage' | 'last_occurrence' | 'severity' | 'status_code';
  sort_order?: 'asc' | 'desc';
  status_code?: number;
  severity?: string;
  min_percentage?: number;
  max_percentage?: number;
  min_count?: number;
  max_count?: number;
  search?: string;
}

export const getHttpErrorsDrillDown = async (
  siteId: number,
  params?: Omit<HttpErrorsDrillDownParams, 'site_id'>
): Promise<HttpErrorsDrillDownResponse> => {
  const queryParams: Record<string, string> = {};

  if (params?.quarter_id) {
    queryParams.quarter_id = params.quarter_id.toString();
  }
  if (params?.period) {
    queryParams.period = params.period;
  }
  if (params?.from_date) {
    queryParams.from_date = params.from_date;
  }
  if (params?.to_date) {
    queryParams.to_date = params.to_date;
  }
  if (params?.page) {
    queryParams.page = params.page.toString();
  }
  if (params?.page_size) {
    queryParams.page_size = params.page_size.toString();
    queryParams.limit = params.page_size.toString();
  }
  if (params?.sort_by) {
    queryParams.sort_by = params.sort_by;
  }
  if (params?.sort_order) {
    queryParams.sort_order = params.sort_order;
  }
  if (params?.status_code) {
    queryParams.status_code = params.status_code.toString();
  }
  if (params?.severity) {
    queryParams.severity = params.severity;
  }
  if (params?.min_percentage !== undefined) {
    queryParams.min_percentage = params.min_percentage.toString();
  }
  if (params?.max_percentage !== undefined) {
    queryParams.max_percentage = params.max_percentage.toString();
  }
  if (params?.min_count !== undefined) {
    queryParams.min_count = params.min_count.toString();
  }
  if (params?.max_count !== undefined) {
    queryParams.max_count = params.max_count.toString();
  }
  if (params?.search) {
    queryParams.search = params.search;
  }

  return api.get<HttpErrorsDrillDownResponse>(
    `/sites/${siteId}/performance-analysis/quarters/http-errors`,
    queryParams
  );
};

// HTTP Errors and Threshold Violations Graph API
export interface HttpThresholdGraphResponse {
  data_points: Array<{
    time: string;
    threshold_breaches: number;
    http_errors: number;
  }>;
  period_start: string;
  period_end: string;
  quarter: {
    id: number;
    range: string;
    start_time: string;
    end_time: string;
    total_checks: number;
  };
  site_id: number;
  site_url: string;
  summary: {
    http_errors: number;
    threshold_breaches: number;
    response_threshold: string;
    total_checks: number;
  };
}

export const getHttpThresholdGraph = async (
  siteId: number,
  quarterId: number,
  period: '1d' | '7d' | '15d' | '30d'
): Promise<HttpThresholdGraphResponse> => {
  const queryParams: Record<string, string> = {
    period,
  };

  return api.get<HttpThresholdGraphResponse>(
    `/sites/${siteId}/performance-analysis/quarters/${quarterId}/http-threshold-graph`,
    queryParams
  );
};

const siteService = { 
  getSites, 
  getSiteById, 
  startSite, 
  stopSite, 
  deleteSite, 
  createSite,
  editSite,
  getActivityLogs,
  exportActivityLogsCSV,
  getUptimeStats,
  bulkStartSites,
  bulkPauseSites,
  bulkDeleteSites,
  getCurrentStatus,
  getResponseTimeGraph,
  getPerformanceAnalysis,
  getThresholdViolations,
  createMaintenanceWindow,
  updateMaintenanceWindow,
  deleteMaintenanceWindow,
  getMaintenanceWindowHistory,
  getHttpErrors,
  getThresholdViolationsDrillDown,
  getHttpErrorsDrillDown,
  getHttpThresholdGraph,
  getMonitoringRegions,
  getTimezones,
};

export default siteService;


